import React, { FunctionComponent, memo, useCallback, useContext, useEffect, useRef } from 'react'
import styles from './CommentElement.module.sass'
import { Comment, CommentData } from '../../../../responses/comments'
import { useDerivedState, useRootDispatch, useRootSelector, useUpload } from '../../../../utils/hooks'
import clsx from 'clsx'
import { isNonEmpty } from 'fp-ts/es6/Array'
import AttachIcon from 'files/attach.svg'
import BindIcon from 'files/bind.svg'
import CommentIcon from 'files/comment/comment.svg'
import CommentEditedIcon from 'files/comment/comment-edited.svg'
import EditIcon from 'files/edit.svg'
import DeleteIcon from 'files/delete.svg'
import Album from '../../files/Album/Album'
import Html from '../../../etc/Html/Html'
import Button from '../../../controls/Button/Button'
import { FileData } from '../../../../responses/files'
import { PERMISSIONS } from '../../../../constants/permissions'
import { historyActions as actions, historyActions } from '../../../../redux/global/history/history-actions'
import { NumberParam, useQueryParam } from 'use-query-params'
import scrollIntoView from 'scroll-into-view-if-needed'
import copyToClipboard from 'copy-to-clipboard'
import { pipe } from 'fp-ts/es6/function'
import { ROUTES } from '../../../../http/routes'
import { urlWithParams, urlWithQuery } from '../../../../http/utils'
import { infoToast } from '../../../controls/Toaster/Toaster'
import DownloadIcon from 'files/download-all.svg'
import { filesActions } from '../../../../redux/global/files'
import { getId } from '../../../../utils/entity-utils'
import Menu from '../../../etc/Menu/Menu'
import { useQueries } from '../../../../utils/hooks/useQueries'
import QuillEditor from '../../../etc/Editor/QuillEditor'
import QuoteIcon from 'files/comment/quote.svg'
import QuoteElement from '../QuoteElement/QuoteElement'
import { CommentEditState } from '../../../../redux/global/history/history-reducer'
import { hoverContext, inactiveContext } from '../../../../constants/contexts'
import { difference } from 'fp-ts/Array'
import { eqString } from 'fp-ts/Eq'

interface Props {
    className?: string,
    comment: Comment,
    editable?: boolean,
    openAlbum?: boolean,
    onCopyClick?: () => void,
    onFileClick?: (file: FileData, comment: Comment) => void,
}

const CommentElement: FunctionComponent<Props> = ({comment, onFileClick, ...props}) => {

    comment.text = comment.text.replace(/\n/g, '<br />')
    const dispatch = useRootDispatch()
    const canEdit = useRootSelector(state =>
        state.global.user.data?.id == comment.user_id ||
        difference(eqString)([PERMISSIONS.COMMENT.FULL_ACCESS], state.global.user.data.permissions)
    )
    const edit = useRootSelector(state => CommentEditState.is.update(state.global.history.editState) && state.global.history.editState.comment.id == comment.id
    )
    const quote = useRootSelector(state => state.global.history.quote)
    const filters = useRootSelector(state => state.global.history.filters)

    const ref = useRef<HTMLDivElement>(null)
    const [param, setParam] = useQueryParam('comment', NumberParam)
    const {isMobile} = useQueries()
    const [text, setText] = useDerivedState(comment.text)
    const [resulted, setResulted] = useDerivedState(comment.resulted)

    const hover = useContext(hoverContext)
    const upload = useUpload(comment.files)

    useEffect(() => {
        if (param == comment.id) {
            ref.current && scrollIntoView(ref.current, {block: 'center'})
            const parent = ref.current?.parentElement
            if (parent) {
                parent.style.background = '#E6F7FD'
            }
            setParam(undefined)
        }
    }, [param, comment.id, setParam])

    const onSend = useCallback(
        (data: CommentData) => dispatch(actions.comment.request({filters, data})),
        [dispatch, filters]
    )

    const onCopy = useCallback(() => {
        copyToClipboard(pipe(
            window.location.host + ROUTES.TASK,
            urlWithParams({taskId: comment.owner_id}),
            urlWithQuery({comment: comment.id})
        ))
        infoToast('Ссылка на комментарий скопирована в буфер обмена')
    }, [comment.id, comment.owner_id])

    const onEdit = useCallback(() => dispatch(historyActions.startEdit(comment)), [dispatch, comment])
    const onDelete = useCallback(() => dispatch(historyActions.startDelete(comment)), [dispatch, comment])

    const fileClick = useCallback(
        (file: FileData) => onFileClick?.(file, comment),
        [comment, onFileClick]
    )

    const onDownload = useCallback(() => {
        if (comment.files.length > 1) {
            dispatch(filesActions.downloadGroup(comment.files.map(getId)))
        } else {
            window.location.href = urlWithParams({filename: comment.files[0].filename})(ROUTES.FILE_ORIGINAL)
        }
    }, [dispatch, comment.files])

    const onQuote = useCallback(() => {
        dispatch(historyActions.quote(comment))
    }, [dispatch, comment])

    return <div ref={ref} className={clsx(props.className, styles.comment, {[styles.active]: edit || hover})}>
        {
            edit ?
                <QuillEditor
                    editMode
                    className={styles.editor}
                    value={text}
                    onChange={setText}
                    resulted={resulted}
                    onResultedChange={setResulted}
                    upload={upload}
                    onSend={onSend}
                    onCancel={() => dispatch(historyActions.cancelEdit())}
                    quote={quote}
                    onQuoteDelete={() => dispatch(historyActions.quote(null))}
                /> :
                <>
                    {
                        comment?.ref && <>
                            <QuoteIcon className={styles.icon}/>
                            <QuoteElement className={styles.content} comment={comment.ref}/>
                        </>
                    }
                    {
                        comment?.text && <>
                            {
                                comment.edited ?
                                    <CommentEditedIcon className={styles.icon}/> :
                                    <CommentIcon className={styles.icon}/>
                            }
                            <Html className={styles.content} html={comment.text} truncate={!props.editable}/>
                        </>
                    }
                    {
                        comment?.files && isNonEmpty(comment.files) && <>
                            <AttachIcon className={styles.icon}/>
                            <Album
                                open={props.openAlbum}
                                className={styles.content}
                                files={comment.files}
                                onClick={fileClick}
                            />
                        </>
                    }
                </>
        }
        {
            props.editable && <>
                <div className={styles.actions}>
                    <inactiveContext.Provider value={false}>
                        {
                            !props.openAlbum && <Button
                                title={'Цитировать'}
                                onClick={onQuote}
                            >
                                <QuoteIcon/>
                            </Button>
                        }
                        {
                            (!!comment?.files.length && !comment.text) && <Button
                                title={'Скачать файлы комментария'}
                                onClick={onDownload}
                            >
                                <DownloadIcon/>
                            </Button>
                        }
                        {
                                !edit && comment.owner_type === 'task' && <Button
                                    key={'copy'}
                                    title={'Скопировать ссылку'}
                                    onClick={onCopy}
                                >
                                    <BindIcon/>
                                </Button>
                        }
                    </inactiveContext.Provider>
                    {
                        !edit && canEdit && <>
                            <Button
                                key={'edit'}
                                title={'Редактировать'}
                                active={edit}
                                onClick={onEdit}
                            >
                                <EditIcon/>
                            </Button>
                            <Button
                                key={'delete'}
                                title={'Удалить'}
                                onClick={onDelete}
                            >
                                <DeleteIcon/>
                            </Button>
                        </>
                    }
                </div>
                <inactiveContext.Provider value={false}>
                    {
                        (!!comment?.files.length && comment.text) && <div className={styles.download}>
                            <Button title={'Скачать файлы комментария'} onClick={onDownload}>
                                <DownloadIcon/>
                            </Button>
                        </div>
                    }
                </inactiveContext.Provider>
            </>
        }
        {
            isMobile && !edit && props.editable && <Menu className={styles.menu} items={[
                {
                    icon: <QuoteIcon/>,
                    onClick: onQuote,
                },
                {
                    icon: <BindIcon/>,
                    onClick: onCopy,
                },
                {
                    icon: <EditIcon/>,
                    onClick: onEdit,
                    hide: !canEdit,
                },
                {
                    icon: <DeleteIcon/>,
                    onClick: onDelete,
                    hide: !canEdit
                },
                {
                    icon: <DownloadIcon/>,
                    hide: !comment?.files.length,
                    onClick: onDownload,
                }
            ]}/>
        }
    </div>
}

export default memo(CommentElement)